/**
 * 搜索结果骨架屏组件
 *
 * CLS 优化：为搜索结果区域提供布局稳定的骨架屏
 *
 * 问题：点击 "View/Download" 后，搜索结果区域从无到有突然出现，导致下方内容位移，CLS 较高
 *
 * 解决：在 loading 时显示与 SearchResultContent 布局一致的骨架屏，保持容器高度稳定
 *
 * 支持的 tabFunc：
 * - "post" (默认): Posts 布局（统计卡片 + 3 列正方形网格）
 * - "story": Stories 布局（标题 + 筛选按钮 + 4 列竖向矩形网格）
 * - "highlights": Highlights 布局（标题 + 圆形头像网格）
 */
import { Skeleton } from 'antd';
import styles from './index.module.less';

interface SearchResultSkeletonProps {
  tabFunc?: 'story' | 'post' | 'highlights';
}

export default function SearchResultSkeleton({ tabFunc = 'post' }: SearchResultSkeletonProps) {
  return (
    <div className={styles.content}>
      {/* Anonymous view 标签 - 与真实内容一致 */}
      <div className={styles.anonymousView}>
        <Skeleton.Input active style={{ width: 120, height: 20 }} />
      </div>

      <div className={styles.bottomContainer}>
        {/* 用户信息区域骨架 */}
        <div className={styles.infContainer}>
          {/* 头像 80x80 */}
          <Skeleton.Avatar active size={80} />
          {/* 用户名和链接 */}
          <div className={styles.info}>
            <Skeleton.Input active style={{ width: 150, height: 28, marginBottom: 8 }} />
            <Skeleton.Input active style={{ width: 200, height: 20 }} />
          </div>
        </div>

        {/* 注意：下载区域取决于用户是否 unlimited，骨架屏不显示避免 CLS */}

        {/* Tab 区域骨架 */}
        <div className={styles.tabContainer}>
          <Skeleton.Input active style={{ width: 80, height: 24 }} />
          <Skeleton.Input active style={{ width: 80, height: 24 }} />
          <Skeleton.Input active style={{ width: 100, height: 24 }} />
        </div>

        {tabFunc === 'story' ? (
          /* Stories 内容骨架 - 标题 + 筛选按钮 + 4 列竖向矩形网格 */
          <>
            {/* 标题骨架：{count} New Stories */}
            <div className={styles.storiesSectionTitle}>
              <Skeleton.Input active style={{ width: 130, height: 20 }} />
            </div>

            {/* 筛选按钮骨架：All / Mentions / Links */}
            <div className={styles.storyFilters}>
              <Skeleton.Button active style={{ width: 50, height: 32, borderRadius: 16 }} />
              <Skeleton.Button active style={{ width: 140, height: 32, borderRadius: 16 }} />
              <Skeleton.Button active style={{ width: 120, height: 32, borderRadius: 16 }} />
            </div>

            {/* Stories 网格骨架 - 4 列竖向矩形 (aspect-ratio: 240/427) */}
            <div className={styles.storiesGrid}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className={styles.storyItem}>
                  <div className={styles.storyItemInner} />
                  <Skeleton.Input active style={{ width: 100, height: 14, marginTop: 8 }} />
                </div>
              ))}
            </div>
          </>
        ) : tabFunc === 'highlights' ? (
          /* Highlights 内容骨架 - 圆形头像网格 */
          <>
            {/* 标题骨架：{count} New Highlights */}
            <div className={styles.highlightsSectionTitle}>
              <Skeleton.Input active style={{ width: 150, height: 20 }} />
            </div>

            {/* 圆形头像网格骨架 */}
            <div className={styles.highlightsGrid}>
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className={styles.highlightItem}>
                  <Skeleton.Avatar active size={100} className={styles.highlightAvatar} />
                  <Skeleton.Input active style={{ width: 80, height: 14, marginTop: 10 }} />
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Posts 内容骨架 - 统计卡片 + 正方形网格 */
          <>
            {/* PostsAnalysis 统计区域骨架 */}
            <div className={styles.postsAnalysis}>
              <div className={styles.analysisLeft}>
                <div className={styles.analysisImage} />
                <div className={styles.analysisData}>
                  <Skeleton.Input active style={{ width: 150, height: 20, marginBottom: 24 }} />
                  <div className={styles.statsRow}>
                    <div>
                      <Skeleton.Input active style={{ width: 60, height: 28, marginBottom: 4 }} />
                      <Skeleton.Input active style={{ width: 40, height: 14 }} />
                    </div>
                    <div>
                      <Skeleton.Input active style={{ width: 60, height: 28, marginBottom: 4 }} />
                      <Skeleton.Input active style={{ width: 60, height: 14 }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.analysisRight}>
                <Skeleton.Input active style={{ width: 80, height: 20, marginBottom: 24 }} />
                <div className={styles.statsRow}>
                  <div>
                    <Skeleton.Input active style={{ width: 60, height: 28, marginBottom: 4 }} />
                    <Skeleton.Input active style={{ width: 50, height: 14 }} />
                  </div>
                  <div>
                    <Skeleton.Input active style={{ width: 60, height: 28, marginBottom: 4 }} />
                    <Skeleton.Input active style={{ width: 80, height: 14 }} />
                  </div>
                </div>
              </div>
            </div>

            {/* 内容网格骨架 - Posts 默认 12 个，3 列正方形 */}
            <div className={styles.gridContainer}>
              {Array.from({ length: 12 }, (_, i) => (
                <div key={i} className={styles.gridItem}>
                  <div className={styles.gridItemInner} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* 底部按钮骨架 */}
        <div className={styles.buttonContainer}>
          <Skeleton.Button active style={{ width: 280, height: 44 }} />
        </div>

        {/* 底部提示文字骨架 */}
        <div className={styles.tipContainer}>
          <Skeleton.Input active style={{ width: 400, height: 16 }} />
        </div>
      </div>
    </div>
  );
}
